import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Document, RadioOrSelectOption } from '../../../../types/common.types';
import { Collection, ParentProgrammeGeneralDetails, Resource } from '../../../../types/entity.types';

import { ACTIVE, ARCHIVED } from '../../../../utils/constants';
import { getClassNames, noOp, openInNewTab } from '../../../../utils/util';

import ProgramLayout from '../../../../layouts/programme/ProgrammeLayout';
import Button from '../../../common/button/Button';
import Dropdown from '../../../common/input/dropdown/Dropdown';
import FilePicker from '../../../common/input/file-picker/FilePicker';
import TextArea from '../../../common/input/text-area/TextArea';
import TextInput from '../../../common/input/text-input/TextInput';
import Modal from '../../../common/modal/Modal';
import ResourceViews from './resource-views/ResourceViews';

import { ReactComponent as FileIcon } from '../../../../resources/icons/file.svg';
import { ReactComponent as LinkIcon } from '../../../../resources/icons/link.svg';
import { ReactComponent as PasteIcon } from '../../../../resources/icons/paste.svg';
import { ReactComponent as PinnedIcon } from '../../../../resources/icons/pinned.svg';
import { ReactComponent as TrashIcon } from '../../../../resources/icons/trash.svg';
import { ReactComponent as UnpinnedIcon } from '../../../../resources/icons/unpinned.svg';
import { ReactComponent as UploadIcon } from '../../../../resources/icons/upload.svg';

import './plan.scss';

type Props = {
  status: string;
  collections?: Collection[];
  totalMembers: number;
  parentProgramme?: ParentProgrammeGeneralDetails;
  createCollection: (
    collection: Collection,
    onSaveCollectionSuccess: (externalReference: string) => void,
    subProgrammeExternalReference?: string
  ) => void;
  updateCollection: (
    collection: Collection,
    onSaveCollectionSuccess?: () => void,
    subProgrammeExternalReference?: string
  ) => void;
  deleteCollection: (externalReference: string, subProgrammeExternalReference?: string) => void;
  createResource: (
    collectionExternalReference: string,
    resource: Resource,
    onSaveResourceSuccess: () => void,
    subProgrammeExternalReference?: string
  ) => void;
  updateResource: (
    resource: Resource,
    onSaveResourceSuccess?: () => void,
    subProgrammeExternalReference?: string
  ) => void;
  deleteResource: (externalReference: string, uuid: string, subProgrammeExternalReference?: string) => void;
  downloadDocument: (resource: Resource) => void;
  switchSubProgramme: (externalReference: string) => void;
};

const Plan = ({
  status,
  collections = [],
  totalMembers,
  parentProgramme,
  createCollection,
  updateCollection,
  deleteCollection,
  createResource,
  updateResource,
  deleteResource,
  downloadDocument,
  switchSubProgramme
}: Props) => {
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionExternalReference, setCollectionExternalReference] = useState<string>();
  const [collectionName, setCollectionName] = useState<string>('');
  const [collectionDescription, setCollectionDescription] = useState<string | undefined>('');
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceUUID, setResourceUUID] = useState<string>();
  const [resourceName, setResourceName] = useState('');
  const [resourceLink, setResourceLink] = useState<string | undefined>('');
  const [resourceFile, setResourceFile] = useState<Document | File>();
  const [resourceFileType, setResourceFileType] = useState<RadioOrSelectOption>();

  const [subProgramme, setSubProgramme] = useState<RadioOrSelectOption>();

  const fileRef = useRef<HTMLInputElement>(null);

  const resourceFileTypeOptions = useRef([
    { label: 'General', value: 'general' },
    { label: 'Meal Plan', value: 'meal-plan' }
  ]);

  const [searchParams] = useSearchParams();

  const subProgrammeOptions = useMemo(() => {
    if (!parentProgramme?.subProgrammeSummaries) return [];

    const options = parentProgramme.subProgrammeSummaries.map((subProgramme) => ({
      label: subProgramme.name,
      value: subProgramme.externalReference
    }));

    setSubProgramme(options[0]);

    return options;
  }, [parentProgramme]);

  const handleSubProgrammeChange = (option: RadioOrSelectOption) => {
    if (option.value === subProgramme?.value) return;

    setSubProgramme(option);
    switchSubProgramme(option.value);
  };

  const onSaveCollectionSuccess = (externalReference?: string) => {
    setCollectionExternalReference(externalReference);
    setCollectionName('');
    setCollectionDescription('');
    setShowCollectionModal(false);
    if (externalReference) {
      setShowResourceModal(true);
    }
  };

  const handleSaveCollectionClick = () => {
    if (collectionExternalReference) {
      const foundCollection = collections.find(
        (collection) => collection.externalReference === collectionExternalReference
      );

      const collection: Collection = {
        ...foundCollection,
        name: collectionName,
        description: collectionDescription
      };
      updateCollection(collection, onSaveCollectionSuccess, subProgramme?.value);
    } else {
      const collection: Collection = {
        name: collectionName,
        description: collectionDescription
      };
      createCollection(collection, onSaveCollectionSuccess, subProgramme?.value);
    }
  };

  const handleCancelAddCollectionClick = () => {
    setShowCollectionModal(false);
    setCollectionExternalReference(undefined);
    setCollectionName('');
    setCollectionDescription('');
  };

  const onSaveResourceSuccess = () => {
    setShowResourceModal(false);
    setResourceName('');
    setResourceLink('');
    setResourceFile(undefined);
    setResourceFileType(undefined);
    setCollectionExternalReference(undefined);
    setResourceUUID(undefined);
  };

  const handleSaveResourceClick = async () => {
    if (resourceUUID) {
      const foundCollection = collections.find((collection) => {
        return collection.externalReference === collectionExternalReference;
      });

      if (!foundCollection) {
        return;
      }

      const foundResource = foundCollection?.linkedMedias?.find((resource) => resource.uuid === resourceUUID);

      if (!foundResource) {
        return;
      }

      const resource: Resource = {
        ...foundResource,
        displayName: resourceName
      };

      if (resourceFile) {
        resource.linkedMediaType = 'Document';
        resource.link = undefined;
        resource.documentType = resourceFileType?.value;
        if ((resourceFile as Document).documentExternalReference) {
          resource.documentExternalReference = (resourceFile as Document).documentExternalReference;
        } else {
          resource.document = resourceFile;
          resource.name = resourceFile.name;
        }
      } else {
        resource.linkedMediaType = 'Link';
        resource.link = resourceLink;
        resource.documentExternalReference = undefined;
      }

      updateResource(resource, onSaveResourceSuccess, subProgramme?.value);
    } else {
      const resource: Resource = {
        displayName: resourceName,
        pinned: false
      };

      if (resourceFile) {
        resource.document = resourceFile;
        resource.documentType = resourceFileType?.value;
        resource.linkedMediaType = 'Document';
        resource.name = resourceFile.name;
      } else {
        resource.link = resourceLink;
        resource.linkedMediaType = 'Link';
      }

      if (!collectionExternalReference) {
        return;
      }
      createResource(collectionExternalReference, resource, onSaveResourceSuccess, subProgramme?.value);
    }
  };

  const handleCancelAddResourceClick = () => {
    setShowResourceModal(false);
    setResourceName('');
    setResourceLink('');
    setResourceFile(undefined);
    setResourceFileType(undefined);
    setCollectionExternalReference(undefined);
    setResourceUUID(undefined);
  };

  const handleFileChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.files) return;
    setResourceFile(target.files[0]);
    setResourceLink('');
  };

  const handleLinkChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setResourceLink(target.value);
    setResourceFile(undefined);
    setResourceFileType(undefined);
  };

  const handlePasteClick = async () => {
    setResourceFile(undefined);
    setResourceFileType(undefined);
    const clipboardContents = await navigator.clipboard.readText();
    setResourceLink(clipboardContents ? clipboardContents : '');
  };

  const handleCollectionClick = (collection: Collection) => {
    setCollectionExternalReference(collection.externalReference);
    setCollectionName(collection.name);
    setCollectionDescription(collection.description);
    setShowCollectionModal(true);
  };

  const handleResourceClick = (resource: Resource) => {
    setCollectionExternalReference(resource.collectionExternalReference);
    setResourceUUID(resource.uuid);
    setResourceName(resource.displayName ?? '');

    if (resource.documentExternalReference) {
      const type = resourceFileTypeOptions.current.find((option) => option.value === resource.documentType);
      if (!type) {
        return;
      }
      setResourceFileType(type);
      setResourceFile({ name: resource.name ?? '', documentExternalReference: resource.documentExternalReference });
    } else {
      setResourceLink(resource.link);
    }
    setShowResourceModal(true);
  };

  const handleResourceIconClick = (resource: Resource) => {
    if (resource.documentExternalReference) {
      downloadDocument(resource);
    } else {
      openInNewTab(resource.link);
    }
  };

  const handlePinCollectionClick = (collection: Collection) => {
    if (status === ARCHIVED) return;

    const copyOfCollection: Collection = { ...collection };
    copyOfCollection.pinned = !copyOfCollection.pinned;

    updateCollection(copyOfCollection, noOp, subProgramme?.value);
  };

  const handlePinResourceClick = (resource: Resource) => {
    if (status === ARCHIVED) return;

    const copyOfResource: Resource = { ...resource };
    copyOfResource.pinned = !copyOfResource.pinned;

    if (copyOfResource.documentExternalReference) {
      copyOfResource.linkedMediaType = 'Document';
    } else {
      copyOfResource.linkedMediaType = 'Link';
    }

    updateResource(copyOfResource, noOp, subProgramme?.value);
  };

  const renderResources = (collection: Collection) => {
    if (!collection.linkedMedias) return <React.Fragment />;

    return collection.linkedMedias.map((resource, index) => (
      <div className="plan__resource" key={index}>
        <div className="plan__resource__info">
          <div className="plan__resource__icon" onClick={() => handleResourceIconClick(resource)}>
            {resource.documentExternalReference && <FileIcon data-testid="file-icon" />}
            {resource.link && <LinkIcon data-testid="link-icon" />}
          </div>
          <div className="plan__resource__details" onClick={() => handleResourceClick(resource)}>
            {resource.documentExternalReference && (
              <p className="plan__resource__name">{resource.displayName ? resource.displayName : resource.name}</p>
            )}
            {resource.link && (
              <p className="plan__resource__name">{resource.displayName ? resource.displayName : resource.link}</p>
            )}
            <p className="plan__resource__type">{resource.documentExternalReference ? 'File' : 'Link'}</p>
          </div>
        </div>
        <div className="plan__resource__actions">
          {(status === ACTIVE || status === ARCHIVED) && (
            <ResourceViews viewers={resource.viewers ?? []} memberCount={totalMembers} archived={status === ARCHIVED} />
          )}
          {resource.pinned && (
            <PinnedIcon
              className={getClassNames('plan__pinned-icon', { disabled: status === ARCHIVED })}
              data-testid="resource-pinned-icon"
              onClick={() => handlePinResourceClick(resource)}
            />
          )}
          {!resource.pinned && (
            <UnpinnedIcon
              className={getClassNames('plan__unpinned-icon', { disabled: status === ARCHIVED })}
              data-testid="resource-unpinned-icon"
              onClick={() => handlePinResourceClick(resource)}
            />
          )}
          {status !== ARCHIVED && (
            <TrashIcon
              className="plan__trash-icon"
              onClick={() =>
                deleteResource(collection.externalReference ?? '', resource.uuid ?? '', subProgramme?.value)
              }
              data-testid="resource-trash-icon"
            />
          )}
        </div>
      </div>
    ));
  };

  const renderCollections = () => {
    return collections.map((collection, index) => (
      <div className="plan__collection" key={index}>
        <div className="plan__collection__details">
          <div onClick={() => handleCollectionClick(collection)}>
            <p className="plan__collection__number">COLLECTION {index + 1}</p>
            <p className="plan__collection__name">{collection?.name?.toUpperCase()}</p>
          </div>
          <div className="plan__collection__actions">
            {collection.pinned && (
              <PinnedIcon
                className={getClassNames('plan__pinned-icon', { disabled: status === ARCHIVED })}
                onClick={() => handlePinCollectionClick(collection)}
                data-testid="collection-pinned-icon"
              />
            )}
            {!collection.pinned && (
              <UnpinnedIcon
                className={getClassNames('plan__unpinned-icon', { disabled: status === ARCHIVED })}
                onClick={() => handlePinCollectionClick(collection)}
                data-testid="collection-unpinned-icon"
              />
            )}
            {status !== ARCHIVED && (
              <TrashIcon
                className="plan__trash-icon"
                onClick={() => deleteCollection(collection.externalReference ?? '', subProgramme?.value)}
                data-testid="collection-trash-icon"
              />
            )}
          </div>
        </div>
        {collection?.description && <p className="plan__collection__description">{collection?.description}</p>}
        {renderResources(collection)}
        {status !== ARCHIVED && (
          <Button
            text="Add Resource"
            onClick={() => {
              setCollectionExternalReference(collection.externalReference);
              setShowResourceModal(true);
            }}
          />
        )}
      </div>
    ));
  };

  const collectionIndex = collectionExternalReference
    ? collections.findIndex(({ externalReference }) => externalReference === collectionExternalReference)
    : collections.length;
  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  return (
    <ProgramLayout>
      <div className="plan" data-testid={`plan-${status}`}>
        {hasSubProgrammes && (
          <div className="plan__sub-programme">
            <Dropdown
              name="subProgramme"
              value={subProgramme}
              options={subProgrammeOptions}
              onChange={handleSubProgrammeChange}
            />
          </div>
        )}

        {status !== ARCHIVED && <Button text="Add Collection" onClick={() => setShowCollectionModal(true)} />}

        <div
          className={getClassNames('plan__collection__list-container', {
            archived: status === ARCHIVED
          })}
        >
          {renderCollections()}
        </div>
      </div>

      <Modal show={showCollectionModal}>
        <div className="plan__modal">
          <div className="plan__modal__header">Collection {collectionIndex + 1}</div>
          <div className="plan__modal__input-container">
            <TextInput
              label="Collection Name:"
              name="collection-name"
              value={collectionName ?? ''}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) => setCollectionName(target.value)}
              disabled={status === ARCHIVED}
            />
            <TextArea
              label="Description (Optional):"
              name="collection-description"
              value={collectionDescription ?? ''}
              onChange={({ target }: ChangeEvent<HTMLTextAreaElement>) => setCollectionDescription(target.value)}
              disabled={status === ARCHIVED}
            />
          </div>
          <div className="plan__modal__button-container">
            <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={handleCancelAddCollectionClick} />
            {status !== ARCHIVED && (
              <Button text="Save" onClick={handleSaveCollectionClick} disabled={!collectionName} />
            )}
          </div>
        </div>
      </Modal>

      <Modal show={showResourceModal}>
        <div className="plan__modal">
          <div className="plan__modal__header">Add Resource</div>
          <div className="plan__modal__input-container">
            <div className="plan__modal__resource-input-container">
              <FilePicker
                triggerElement={
                  <div className="plan__modal__resource-input">
                    <FileIcon />
                    <input
                      type="text"
                      value={resourceFile ? resourceFile.name : ''}
                      placeholder="Click button to upload"
                      disabled
                    />
                    <div className="plan__modal__resource-input__icon" onClick={() => fileRef?.current?.click()}>
                      <UploadIcon />
                    </div>
                  </div>
                }
                fileRef={fileRef}
                onChange={handleFileChange}
              />
              <Dropdown
                value={resourceFileType}
                options={resourceFileTypeOptions.current}
                onChange={(option: RadioOrSelectOption) => setResourceFileType(option)}
                placeholder="Select Type..."
              />
              <span>Or</span>
              <div className="plan__modal__resource-input">
                <LinkIcon />
                <input
                  type="text"
                  value={resourceLink}
                  onChange={handleLinkChange}
                  placeholder="Paste/Type link here"
                />
                <div className="plan__modal__resource-input__icon" onClick={handlePasteClick} data-testid="paste-icon">
                  <PasteIcon />
                </div>
              </div>
            </div>
            <TextInput
              label="Custom File/Link Name (Optional):"
              name="resource-name"
              value={resourceName}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) => setResourceName(target.value)}
            />
          </div>
          <div className="plan__modal__button-container">
            <Button text="Cancel" onClick={handleCancelAddResourceClick} />
            <Button text="Save" onClick={handleSaveResourceClick} disabled={!resourceFile && !resourceLink} />
          </div>
        </div>
      </Modal>
    </ProgramLayout>
  );
};

export default Plan;
